import { Link, useNavigate } from "@tanstack/react-router";
import { EmptyState } from "@client/components/EmptyState";
import { Panel } from "@client/components/Panel";
import { ProductForm } from "@client/components/ProductForm";
import { useAuth } from "@client/hooks/useAuth";
import { useCreateProduct } from "@client/hooks/useProducts";
import { buttonClass } from "@client/lib/buttonStyles";

/**
 * Creating a product also creates its baseline scenario, in one transaction on
 * the server — so the product this lands on always has something to fork.
 */
export function ProductNewPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const create = useCreateProduct();

  // The server refuses this for a VIEWER regardless; saying so here means they
  // read a sentence instead of filling in a form that was never going to save.
  if (!auth.permissions.canCreateProduct) {
    return (
      <EmptyState
        title="Products are created by product owners"
        description="Your role can build scenarios against any product here, but not create the products themselves."
        action={
          <Link to="/" className={buttonClass("ghost", "sm")}>
            Back to products
          </Link>
        }
        footnote="An administrator can change your role on the allow list."
      />
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-4">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-bold text-ink">New product</h1>
        <p className="text-sm text-ink-soft">
          The currency and the horizon become the defaults every scenario inherits. The currency is
          frozen once the first scenario is saved, so it is worth getting right now.
        </p>
      </header>

      <Panel>
        <ProductForm
          submitLabel="Create product"
          busy={create.isPending}
          error={create.error}
          onCancel={() => void navigate({ to: "/" })}
          onSubmit={(input) => {
            create.mutate(input, {
              onSuccess: (product) => {
                void navigate({ to: "/products/$slug", params: { slug: product.slug } });
              },
            });
          }}
        />
      </Panel>
    </div>
  );
}
